import { shouldLastPlayStatusChange } from "@/app/(root)/videos/[video]/player-side/helpers";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import services from "@/store/services";

const interval = 10000;

function useVideoMetadata(props = {}) {
  let { playTimerRef = null } = props;

  const videoPreference = useSelector((state) => state.videoPreference);
  const metaDataArray = useSelector((state) => state.videoMetaData);
  const { dayPoints } = useSelector((state) => state.nonAuthWatchTime);
  const loc_points = useSelector((state) => state.loc_points);
  const points = useSelector((state) => state.points);
  const [currentId, setCurrentId] = useState(null);

  const dispatch = useDispatch();

  const earnedDayPointsRef = useRef(null);
  const playMetaRef = useRef(null);
  const metaDataRef = useRef(null);
  const locPointsRef = useRef(null);
  const dayPointsRef = useRef(null);
  const playerRef = useRef(null);

  useEffect(() => {
    if (currentId) {
      let prevmetadata = metaDataArray.find((item) => item.id === currentId);
      playMetaRef.current = prevmetadata;
    }
  }, [metaDataArray, currentId]);

  useEffect(() => {
    earnedDayPointsRef.current = points.earnedPoints;
  }, [points?.earnedPoints]);

  useEffect(() => {
    locPointsRef.current = loc_points;
  }, [loc_points]);

  useEffect(() => {
    dayPointsRef.current = dayPoints;
  }, [dayPoints]);

  useEffect(() => {
    if (metaDataArray) metaDataRef.current = metaDataArray;
  }, [metaDataArray]);

  const onVideoSettingChange = async (playrate) => {
    dispatch(
      services.updateVideoPreference({ playrate: parseFloat(playrate) })
    );
    return;
  };

  const setPlayerRef = (ply) => {
    playerRef.current = ply;
  };

  const videoIntervalFunc = (datakey, interval) => {
    return () => {
      const playrate =
        (playMetaRef.current?.playrate ?? 1)
          ? playMetaRef.current?.playrate >= 1 &&
            playMetaRef.current?.playrate <= 2
            ? playMetaRef.current.playrate
            : 1
          : 1;
      const updatedCurrSec =
        playMetaRef?.current?.currsecs + (interval / 1000) * playrate;

      const lastplaystatus = shouldLastPlayStatusChange(
        playMetaRef.current,
        playerRef.current
      );

      const updatedData = {
        ...datakey,
        currsecs: updatedCurrSec,
        lastplaystatus,
      };
      dispatch(services.updateVideoMetaData(updatedData));
    };
  };

  // add meta data
  const addVideoMetaData = async (data) => {
    setCurrentId(data.id);
    let updatedValue = {
      id: data.id,
      lastplaystatus: data.lastplaystatus || 0,
      initiate: data.initiate || Date.now(),
      currsecs: data.currsecs || 0,
      status: data.status || true,
      views: false,
    };
    dispatch(services.addVideoMetaData(updatedValue));
    //NOTE - Playing State
    if (updatedValue.status) {
      playTimerRef.current = setInterval(
        videoIntervalFunc(updatedValue, interval),
        interval
      );
    } else {
      if (playTimerRef.current) clearInterval(playTimerRef.current);
    }

    return updatedValue;
  };

  const updateVideoMetadata = async (data, playrate) => {
    let prevmetadata = metaDataArray.find((item) => item.id === data.id);
    setCurrentId(data.id);

    playMetaRef.current = prevmetadata;
    let action = null;
    let timestamp = Date.now();
    let prevRate = videoPreference;
    let isRateChange = prevRate.playrate != playrate;

    let datakey;
    let timeSpent = 0;

    if (prevmetadata && prevmetadata.status) {
      //NOTE - Play to Pause Event || Rate Change Event
      if (prevmetadata.status !== data.status || isRateChange) {
        timeSpent = Math.floor(
          (Number(timestamp - prevmetadata.initiate) / 1000) *
            (Number(prevRate.playrate) >= 1 && Number(prevRate.playrate) <= 2
              ? Number(prevRate.playrate)
              : 1)
        );
        action = "clearInterval";
      }

      datakey = {
        ...prevmetadata,
        initiate: timestamp,
        currsecs:
          prevmetadata.status !== data.status || isRateChange //?
            ? prevmetadata.currsecs + timeSpent
            : prevmetadata.currsecs, //previously it's 0 then data.lastplaystatus
        lastplaystatus: data.lastplaystatus,
        status: data.status,
      };
      // cb(timeSpent);
    } else {
      //NOTE - Pause to Play Event
      if (data.status) {
        action = "addInterval";
      }
      datakey = {
        ...data,
        initiate: timestamp, //need to do some changes here. as when we are in false state and then change the resolution then still initiate get updated with current timestamp value.
        status: data.status,
        lastplaystatus: data.lastplaystatus,
      };
    }

    if (
      earnedDayPointsRef.current - locPointsRef.current <= 0 ||
      dayPointsRef.current >=
        Number(process.env.NEXT_PUBLIC_NON_AUTH_TIME_LIMIT)
    ) {
      action = "clearInterval";
    }

    if (isRateChange) action = "resetInterval";
    dispatch(services.updateVideoMetaData(datakey));
    //NOTE - Perform Action
    if (action === "clearInterval") {
      if (playTimerRef.current) clearInterval(playTimerRef.current);
    } else if (action === "addInterval") {
      playTimerRef.current = setInterval(
        videoIntervalFunc(datakey, interval),
        interval
      );
    } else if (action === "resetInterval") {
      if (playTimerRef.current) {
        clearInterval(playTimerRef.current);
        playTimerRef.current = setInterval(
          videoIntervalFunc(datakey, interval),
          interval
        );
      }
    }

    if (isRateChange) onVideoSettingChange(playrate);
    return datakey;
  };

  const getVideoMetadataById = (id) => {
    return metaDataArray.find((item) => item.id === id);
  };

  const isviewsCount = async (id, totallength, meta) => {
    let metadata;
    if (meta) metadata = meta;
    else metadata = getVideoMetadataById(id);

    let seconds = parseFloat(
      Math.floor(totallength) * 60 +
        (totallength - Math.floor(totallength)) * 100
    );
    if (
      metadata &&
      metadata.currsecs >=
        parseFloat(process.env.NEXT_PUBLIC_VIEWS_CRITERIA * seconds)
    ) {
      let data = {
        ...metadata,
        id,
        views: true,
      };
      dispatch(services.updateVideoMetaData(data));
      return true;
    }
    return false;
  };

  const shouldUpdateProgress = async (id, totallength, meta) => {
    let metadata;
    if (meta) metadata = meta;
    else metadata = getVideoMetadataById(id);

    let seconds =
      Math.floor(totallength) * 60 +
      (totallength - Math.floor(totallength)) * 60;

    if (
      metadata?.currsecs >=
      parseFloat(process.env.NEXT_PUBLIC_VIDEO_COMPLETION_CRITERIA) *
        Math.floor(seconds)
    ) {
      return metadata.currsecs;
    }
    return 0;
  };

  return {
    addVideoMetaData,
    updateVideoMetadata,
    getVideoMetadataById,
    isviewsCount,
    shouldUpdateProgress,
    setPlayerRef,
    videoMetaData: metaDataArray,
  };
}

export default useVideoMetadata;
